import React, { useState, useContext, useMemo, useEffect } from "react";
import { Users, Eye, Edit2, Trash2, FileText } from "lucide-react";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { useEmployeeFilters } from "../hooks/useEmployeeFilters";
import { useEmployeeProfilePics } from "../hooks/useEmployeeProfilePics";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { text } from "../constants/text";
import FiltersPanel from "./FiltersPanel";
import { getCardStyle, getThHeaderStyle, getTdStyle } from "../../(Admin)Attendance/styles/attendanceStyles";
import EmployeeAvatarCell from "../../../components/EmployeeAvatarCell";
import { useVisibleColumns } from "../../../hooks/useVisibleColumns";
import Pagination from "../../../components/Pagination";

export default function EmployeeTableIntegrated({
  employees: allEmployees = [],
  loading = false,
  error = "",
  clientsList = [],
  companies = [],
  onViewEmployee,
  onEditEmployee,
  onDeleteEmployee,
  onViewDocuments,
  onFilteredChange,
  canEditEmployee = true,
  canDeleteEmployee = true,
  canViewDocuments = true,
  actionButtons,
}) {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const {
    searchTerm,
    setSearchTerm,
    filterDepartment,
    setFilterDepartment,
    filterStatus,
    setFilterStatus,
    filterCompany,
    setFilterCompany,
    showAllEmployees,
  } = useEmployeeFilters();
  const { employeeProfilePics, fetchProfilePic, setEmployeeProfilePics } = useEmployeeProfilePics(allEmployees);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getScreenSize = () => {
    if (typeof window === "undefined") return "md";
    const width = window.innerWidth;
    if (width < 640) return "xs";
    if (width < 768) return "sm";
    if (width < 1024) return "md";
    if (width < 1280) return "lg";
    if (width < 1536) return "xl";
    return "xxl";
  };

  const [screenSize, setScreenSize] = React.useState(getScreenSize());

  React.useEffect(() => {
    const handleResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define color scheme based on theme
  const style = {
    border:
      theme === "light"
        ? "rgba(26, 26, 26, 0.15)"
        : "rgba(255, 255, 255, 0.15)",
    shadow:
      theme === "light"
        ? "0 1px 3px rgba(0, 0, 0, 0.1)"
        : "0 8px 32px rgba(0, 0, 0, 0.3)",
    bgPrimary: theme === "light" ? "#ffffff" : "rgba(30, 30, 46, 0.8)",
    bgSecondary:
      theme === "light"
        ? "rgba(26, 26, 26, 0.02)"
        : "rgba(255, 255, 255, 0.05)",
    cardBg: theme === "light" ? "#ffffff" : "rgba(10, 10, 161, 0.01)",
    inputBg:
      theme === "light"
        ? "rgba(26, 26, 26, 0.05)"
        : "rgba(255, 255, 255, 0.08)",
    textPrimary: theme === "light" ? "#1a1a1a" : "#ffffff",
    textMuted:
      theme === "light" ? "rgba(26, 26, 26, 0.6)" : "rgba(255, 255, 255, 0.6)",
    textSecondary: theme === "light" ? "#1a1a1a" : "#ffffff",
    accent: "#2e7cba",
    hoverBg:
      theme === "light"
        ? "rgba(59, 130, 246, 0.08)"
        : "rgba(59, 130, 246, 0.15)",
  };
  const employeeColumns = [
    {
      key: "employee",
      label: text[language]?.employee || "Employee",
      borderTopLeftRadius: "1px",
      borderTopRightRadius: "0px",
      style: {
        textAlign: language === "ar" ? "right" : "left",
      },
    },
    ...(screenSize !== "xs"
      ? [{ key: "department", label: text[language]?.department || "Department" }]
      : []),
    ...(screenSize !== "sm" && screenSize !== "xs"
      ? [{ key: "joinDate", label: text[language]?.joinDate || "Join Date" }]
      : []),
    { key: "status", label: text[language]?.Status || "Status" },
    {
      key: "actions",
      label: text[language]?.actions || "Actions",
      // في RTL: آخر عمود يبقى على اليسار
      borderTopLeftRadius: "0px",
      borderTopRightRadius: "1px",
    },
  ];

  // ✅ Admin-level column visibility (hiddenColumns registry). Both the
  // header and the body cells render from this filtered list so they can
  // never get out of sync.
  const visibleColumns = useVisibleColumns("employees", "employeeTable", employeeColumns);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  const filteredEmployees = useMemo(() => {
    const list = Array.isArray(allEmployees) ? allEmployees : [];
    if (showAllEmployees) return list;
    const term = (searchTerm || "").toLowerCase().trim();
    return list.filter((emp) => {
      if (!emp) return false;
      const fullName = (emp.name || "").toLowerCase();
      const arabicName = (emp.arabicName || "").toLowerCase();
      const id = String(emp.id || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      if (
        term &&
        !(
          fullName.includes(term) ||
          arabicName.includes(term) ||
          id.includes(term) ||
          email.includes(term)
        )
      )
        return false;
      if (
        filterDepartment &&
        !(emp.department || "")
          .toLowerCase()
          .includes(filterDepartment.toLowerCase())
      )
        return false;
      if (filterStatus && (emp.status || "") !== filterStatus) return false;
      if (filterCompany && (emp.company || emp.workingCompany || emp.clientName || "") !== filterCompany) return false;
      return true;
    });
  }, [
    allEmployees,
    searchTerm,
    filterDepartment,
    filterStatus,
    filterCompany,
    showAllEmployees,
  ]);

  useEffect(() => {
    if (typeof onFilteredChange === "function") {
      try {
        onFilteredChange(filteredEmployees || []);
      } catch (e) {
        // ignore
      }
    }
  }, [filteredEmployees, onFilteredChange]);

  // Reset to first page when search/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDepartment, filterStatus, filterCompany]);

  const totalPages = Math.ceil((filteredEmployees?.length || 0) / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedEmployees = (filteredEmployees || []).slice(
    startIdx,
    startIdx + itemsPerPage,
  );

  // Load real profile pictures only for the currently visible page (fast + cached, avatar shows instantly)
  useEffect(() => {
    let cancelled = false;
    const codes = displayedEmployees
      .map((e) => e.id)
      .filter((code) => code && !employeeProfilePics[code]);

    (async () => {
      const CONCURRENCY = 6;
      for (let i = 0; i < codes.length && !cancelled; i += CONCURRENCY) {
        await Promise.all(
          codes.slice(i, i + CONCURRENCY).map(async (code) => {
            const url = await fetchProfilePic(code);
            if (!cancelled && url) {
              setEmployeeProfilePics((prev) => (prev[code] ? prev : { ...prev, [code]: url }));
            }
          }),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedEmployees.map((e) => e.id).join(",")]);

  const getStatusBadge = (status) => {
    const statusStyles = {
      Active: {
        bg:
          theme === "dark"
            ? "rgba(16, 185, 129, 0.2)"
            : "rgba(16, 185, 129, 0.1)",
        text: "#10b981",
        label: text[language]?.statusActive || "Active",
      },
      OnLeave: {
        bg:
          theme === "dark"
            ? "rgba(245, 158, 11, 0.2)"
            : "rgba(245, 158, 11, 0.1)",
        text: "#f59e0b",
        label: text[language]?.statusOnLeave || "On Leave",
      },
      Inactive: {
        bg:
          theme === "dark"
            ? "rgba(239, 68, 68, 0.2)"
            : "rgba(239, 68, 68, 0.1)",
        text: "#ef4444",
        label: text[language]?.statusInactive || "Inactive",
      },
    };
    const style = statusStyles[status] || statusStyles.Active;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: style.bg,
          color: style.text,
          border: `1px solid ${style.text}`,
        }}
      >
        {style.label}
      </span>
    );
  };

  const styles = {
    outerWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      marginBottom: "24px",
    },
    filterCard: {
      ...getCardStyle(theme),
      marginBottom: "0px",
    },
    tableCard: {
      ...getCardStyle(theme),
      marginTop: "0px",
    },
    header: {
      padding: screenSize === "xs" ? "16px" : "24px",
      borderBottom: `1px solid ${style.border}`,
      background: style.bgSecondary,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    headerContent: { flex: 1 },
    headerTitle: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: screenSize === "xs" ? "16px" : "20px",
      fontWeight: "700",
      color: style.textPrimary,
      margin: "0 0 8px 0",
    },
    headerSubtitle: {
      fontSize: "13px",
      color: style.textMuted,
      margin: "0",
      marginTop: "8px",
      marginRight: "42px",
    },
    filterBar: {
      padding: screenSize === "xs" ? "12px" : "16px",
      background: "transparent",
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "12px",
    },
    filterGroup: { display: "flex", flexDirection: "column", gap: "6px" },
    filterLabel: {
      fontSize: "14px",
      fontWeight: "600",
      color: style.textPrimary,
    },
    filterInput: {
      padding: "12px 16px",
      border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(148, 163, 184, 0.3)",
      borderRadius: "12px",
      background: theme === "dark" ? "rgba(15, 23, 42, 0.5)" : "rgba(248, 250, 252, 0.8)",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.3s ease",
      outline: "none",
      boxSizing: "border-box",
    },
    tableWrapper: {
      marginTop: "16px",
      overflow: "auto",
      borderRadius: "12px",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth: "900px",
      position: "relative",
      zIndex: 1,
    },
    th: getThHeaderStyle(theme),
    tr: {
      borderBottom: theme === "dark"
        ? "1px solid rgba(59, 130, 246, 0.1)"
        : "1px solid rgba(59, 130, 246, 0.05)",
      transition: "background 0.2s ease",
    },
    td: getTdStyle(theme),
    actionsCell: {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
    },
    actionBtn: {
      display: "none"
    },
    emptyState: {
      textAlign: "center",
      padding: "48px 24px",
      color: style.textMuted,
    },
  };

  if (loading)
    return (
      <div style={styles.tableCard}>
        <div style={styles.emptyState}>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "500",
              color: style.textPrimary,
            }}
          >
            {text[language]?.loading || "Loading..."}
          </p>
        </div>
      </div>
    );
  if (error)
    return (
      <div style={styles.tableCard}>
        <div style={styles.emptyState}>
          <p style={{ fontSize: "16px", color: "#ef4444", fontWeight: "500" }}>
            {text[language]?.errorLoadingEmployees || "Error loading employees"}
          </p>
        </div>
      </div>
    );

  return (
    <div style={styles.outerWrapper}>
      {/* Filters Panel */}
      <div style={styles.filterCard}>
        <FiltersPanel
          theme={theme}
          language={language}
          styles={styles}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterDepartment={filterDepartment}
          setFilterDepartment={setFilterDepartment}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterCompany={filterCompany}
          setFilterCompany={setFilterCompany}
          departmentsList={Array.isArray(allEmployees) ? Array.from(new Set(allEmployees.map(e => e?.department || "").filter(Boolean))).sort() : []}
          companiesList={Array.from(new Set([
            ...(clientsList || []).map(c => c.name),
            ...(companies || []),
            ...(Array.isArray(allEmployees) ? allEmployees.map(e => e?.company || e?.workingCompany || e?.clientName || "") : [])
          ].filter(Boolean))).sort()}
          screenSize={screenSize}
          filteredCount={filteredEmployees?.length || 0}
          totalCount={allEmployees?.length || 0}
          actionButtons={actionButtons}
        />
      </div>

      {/* Table Card */}
      <div style={styles.tableCard}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "700",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexDirection: "row",
            textAlign: language === "ar" ? "right" : "left",
          }}
        >
          <Users size={20} />
          {text[language]?.employeeList || `Employees (${filteredEmployees?.length || 0})`}
        </h3>
        <div style={styles.tableWrapper} className="scrollbar-hidden">
          {displayedEmployees.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} style={styles.th}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedEmployees.map((emp, idx) => {
                  const profilePic = employeeProfilePics?.[emp.id];
                  return (
                    <tr
                      key={emp.id || idx}
                      style={{
                        ...styles.tr,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          theme === "dark"
                            ? "rgba(59, 130, 246, 0.08)"
                            : "rgba(59, 130, 246, 0.04)";
                        e.currentTarget.style.boxShadow =
                          theme === "dark"
                            ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
                            : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {showColumn("employee") && (
                        <td style={{ ...styles.td, textAlign: "left", padding: 0 }}>
                          <div style={{ padding: screenSize === "xs" ? "12px 8px" : "16px 12px" }}>
                            <EmployeeAvatarCell
                              employee={emp}
                              profilePic={profilePic}
                              language={language}
                              theme={theme}
                              onClick={() => onViewEmployee?.(emp)}
                            />
                          </div>
                        </td>
                      )}
                      {showColumn("department") && (
                        <td style={styles.td}>{emp.department || "-"}</td>
                      )}
                      {showColumn("joinDate") && (
                        <td style={styles.td}>
                          {emp.joinDate
                            ? new Date(emp.joinDate).toLocaleDateString()
                            : "-"}
                        </td>
                      )}
                      {showColumn("status") && (
                        <td style={styles.td}>{getStatusBadge(emp.status)}</td>
                      )}
                      {showColumn("actions") && (
                      <td style={styles.td}>
                        <div style={styles.actionsCell}>
                          <button
                            style={btnStyle({ theme, variant: "neutral" })}
                            onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                            onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                            onClick={() => onViewEmployee?.(emp)}
                            title="View"
                          >
                            <Eye size={14} />
                          </button>
                          {canViewDocuments && (
                            <button
                              style={btnStyle({ theme, variant: "neutral" })}
                              onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                              onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                              onClick={() => onViewDocuments?.(emp)}
                              title="Documents"
                            >
                              <FileText size={14} />
                            </button>
                          )}
                          {canEditEmployee && (
                            <button
                              style={btnStyle({ theme, variant: "neutral" })}
                              onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                              onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                              onClick={() => onEditEmployee?.(emp)}
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {canDeleteEmployee && (
                            <button
                              style={btnStyle({ theme, variant: "danger" })}
                              onMouseEnter={(e) => hoverIn(e, { theme, variant: "danger" })}
                              onMouseLeave={(e) => hoverOut(e, { theme, variant: "danger" })}
                              onClick={() => onDeleteEmployee?.(emp.id)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: style.textMuted,
                }}
              >
                {text[language]?.noEmployeesFound || "No employees found"}
              </p>
            </div>
          )}
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          theme={theme}
          isRTL={language === "ar"}
        />
      </div>
    </div>
  );
}

// ── style helpers ──────────────────────────────────────────────────────────────
function btnStyle({ theme, variant }) {
  const isDark = theme === "dark";

  const variants = {
    neutral: {
      bg: isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)",
      color: isDark ? "#f1f5f9" : "#334155",
    },
    danger: {
      bg: isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
  };

  const v = variants[variant] || variants.neutral;

  return {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    background: v.bg,
    color: v.color,
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
  };
}

function hoverIn(e, { theme, variant }) {
  const isDark = theme === "dark";
  if (variant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.4)" : "rgba(254,202,202,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)";
  }
  e.currentTarget.style.transform = "translateY(-2px)";
}

function hoverOut(e, { theme, variant }) {
  const isDark = theme === "dark";
  if (variant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)";
  }
  e.currentTarget.style.transform = "translateY(0)";
}